import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api, formatApiError } from "@/lib/api";
import { Camera, Stop } from "@phosphor-icons/react";

// Phone camera QR scanner + manual pass-number fallback.
export default function GateScanner({ onResult, onLookup }) {
  const [running, setRunning] = useState(false);
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const scanner = useRef(null);
  const lock = useRef(false);

  const submit = async (token) => {
    if (lock.current || !token) return;
    lock.current = true;
    setBusy(true);
    try {
      const { data } = await api.post("/gate-passes/lookup", { token });
      // Move to the photo step first; stopping the camera can hang on iOS Safari.
      onLookup(data);
      stop();
    } catch (err) {
      onResult({ ok: false, error: formatApiError(err) });
    } finally {
      setBusy(false);
      setTimeout(() => { lock.current = false; }, 2500);
    }
  };

  const start = async () => {
    setRunning(true);
    // Let React un-hide the preview box first — iOS Safari renders nothing into a hidden element.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      scanner.current = new Html5Qrcode("gate-qr-reader");
      const box = (w, h) => { const s = Math.floor(Math.min(w, h) * 0.7); return { width: s, height: s }; };
      await scanner.current.start({ facingMode: "environment" }, { fps: 10, qrbox: box }, (text) => submit(text.trim()), () => {});
    } catch (err) {
      setRunning(false);
      onResult({ ok: false, error: `Camera unavailable: ${err?.message || err}. Allow camera access for this site in your phone's browser settings.` });
    }
  };

  const stop = async () => {
    setRunning(false);
    const s = scanner.current;
    scanner.current = null;
    try { if (s?.isScanning) await Promise.race([s.stop(), new Promise((r) => setTimeout(r, 1500))]); } catch { /* already stopped */ }
  };

  useEffect(() => () => { scanner.current?.stop().catch(() => {}); }, []);

  return (
    <div className="space-y-3" data-testid="gate-scanner">
      <div id="gate-qr-reader" data-testid="gate-qr-reader"
        className={`w-full max-w-sm mx-auto bg-black overflow-hidden ${running ? "min-h-[280px]" : "hidden"}`} />
      <button type="button" onClick={running ? stop : start} data-testid="gate-camera-toggle"
        className={`w-full py-4 text-white text-base font-bold flex items-center justify-center gap-2 ${running ? "bg-ink" : "bg-[#047857]"}`}>
        {running ? <><Stop size={20} weight="bold" /> Stop camera</> : <><Camera size={20} weight="bold" /> Scan pass</>}
      </button>
      <form onSubmit={(e) => { e.preventDefault(); submit(manual.trim()); setManual(""); }} className="flex gap-2 min-w-0">
        <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="or type pass no."
          data-testid="gate-manual-input" className="flex-1 min-w-0 px-3 py-3 border border-canvas-line text-sm font-mono uppercase" />
        <button type="submit" disabled={busy || !manual.trim()} data-testid="gate-manual-submit"
          className="px-4 bg-ink text-white text-sm font-semibold disabled:opacity-40">Go</button>
      </form>
    </div>
  );
}
